import type { CanvasElement } from '../../../services/feedWatch';

export interface CanvasTemplate {
  name: string;
  category: 'shopping' | 'social' | 'video' | 'other';
  description: string;
  width: number;
  height: number;
  elements: CanvasElement[];
}

// ── Shared element builders ──────────────────────────────────

function bg(w: number, h: number, fill = '#ffffff'): CanvasElement {
  return { type: 'rect', x: 0, y: 0, width: w, height: h, fill, borderRadius: 0, border: '' };
}

function fullImage(w: number, h: number): CanvasElement {
  return { type: 'image', x: 0, y: 0, width: w, height: h, src: '{{image_link}}', objectFit: 'cover', borderRadius: 0 };
}

function overlay(y: number, w: number, h: number, opacity = 0.65): CanvasElement {
  return { type: 'rect', x: 0, y, width: w, height: h, fill: `rgba(0,0,0,${opacity})`, borderRadius: 0, border: '' };
}

function title(x: number, y: number, w: number, h: number, fontSize: number, color = '#ffffff'): CanvasElement {
  return { type: 'text', x, y, width: w, height: h, content: '{{title}}', fontSize, fontWeight: 'bold', fontFamily: 'Inter, sans-serif', color, textAlign: 'left' };
}

function price(x: number, y: number, w: number, h: number, fontSize: number, color = '#34d399'): CanvasElement {
  return { type: 'text', x, y, width: w, height: h, content: '{{price}}', fontSize, fontWeight: 'bold', fontFamily: 'Inter, sans-serif', color, textAlign: 'left' };
}

function brand(x: number, y: number, w: number, h: number, fontSize: number, color = '#6b7280'): CanvasElement {
  return { type: 'text', x, y, width: w, height: h, content: '{{brand}}', fontSize, fontWeight: 'normal', fontFamily: 'Inter, sans-serif', color, textAlign: 'left' };
}

// ── Templates ────────────────────────────────────────────────

export const CANVAS_TEMPLATES: CanvasTemplate[] = [
  // ─── Shopping ────────────────────────────────────────
  {
    name: 'Google Shopping',
    category: 'shopping',
    description: '1200 x 628',
    width: 1200,
    height: 628,
    elements: [
      bg(1200, 628),
      { type: 'image', x: 40, y: 40, width: 548, height: 548, src: '{{image_link}}', objectFit: 'contain', borderRadius: 12 },
      title(628, 60, 532, 120, 36, '#111827'),
      price(628, 220, 300, 60, 48, '#059669'),
      brand(628, 520, 300, 40, 22),
    ],
  },
  {
    name: 'Price Tag',
    category: 'shopping',
    description: '800 x 400',
    width: 800,
    height: 400,
    elements: [
      { type: 'rect', x: 0, y: 0, width: 800, height: 400, fill: '#fafafa', borderRadius: 16, border: '2px solid #e5e7eb' },
      { type: 'image', x: 24, y: 24, width: 352, height: 352, src: '{{image_link}}', objectFit: 'contain', borderRadius: 12 },
      title(410, 48, 360, 100, 28, '#111827'),
      price(410, 200, 300, 60, 40, '#dc2626'),
    ],
  },
  {
    name: 'Product Showcase',
    category: 'shopping',
    description: '1200 x 1200',
    width: 1200,
    height: 1200,
    elements: [
      bg(1200, 1200, '#f8fafc'),
      { type: 'image', x: 100, y: 60, width: 1000, height: 800, src: '{{image_link}}', objectFit: 'contain', borderRadius: 16 },
      title(100, 900, 1000, 80, 42, '#111827'),
      price(100, 1010, 400, 60, 52, '#059669'),
      brand(100, 1100, 400, 50, 24),
    ],
  },

  // ─── Social — Square / Landscape ─────────────────────
  {
    name: 'Instagram Post',
    category: 'social',
    description: '1080 x 1080',
    width: 1080,
    height: 1080,
    elements: [
      fullImage(1080, 1080),
      overlay(780, 1080, 300),
      title(48, 820, 984, 100, 40),
      price(48, 960, 300, 60, 44),
    ],
  },
  {
    name: 'Facebook Post',
    category: 'social',
    description: '1200 x 630',
    width: 1200,
    height: 630,
    elements: [
      fullImage(1200, 630),
      overlay(400, 1200, 230),
      title(48, 430, 1104, 80, 36),
      price(48, 540, 300, 50, 40),
    ],
  },
  {
    name: 'Facebook Ad',
    category: 'social',
    description: '1080 x 1080',
    width: 1080,
    height: 1080,
    elements: [
      bg(1080, 1080, '#1a1a2e'),
      { type: 'image', x: 90, y: 40, width: 900, height: 700, src: '{{image_link}}', objectFit: 'contain', borderRadius: 16 },
      title(60, 780, 960, 100, 38),
      price(60, 910, 400, 60, 48, '#fbbf24'),
      brand(60, 1000, 400, 40, 22, '#9ca3af'),
    ],
  },
  {
    name: 'Twitter / X Post',
    category: 'social',
    description: '1600 x 900',
    width: 1600,
    height: 900,
    elements: [
      fullImage(1600, 900),
      overlay(600, 1600, 300, 0.7),
      title(60, 640, 1480, 90, 42),
      price(60, 770, 400, 60, 48),
    ],
  },
  {
    name: 'Pinterest Pin',
    category: 'social',
    description: '1000 x 1500',
    width: 1000,
    height: 1500,
    elements: [
      fullImage(1000, 1500),
      overlay(1100, 1000, 400),
      title(48, 1150, 904, 120, 40),
      price(48, 1330, 350, 60, 48, '#fbbf24'),
      brand(48, 1420, 350, 40, 22, '#d1d5db'),
    ],
  },
  {
    name: 'Snapchat Ad',
    category: 'social',
    description: '1080 x 1920',
    width: 1080,
    height: 1920,
    elements: [
      fullImage(1080, 1920),
      overlay(1500, 1080, 420, 0.6),
      title(56, 1550, 968, 120, 44),
      price(56, 1720, 400, 70, 52, '#fbbf24'),
      brand(56, 1830, 400, 50, 24, '#d1d5db'),
    ],
  },

  // ─── Video / Reels (9:16) ────────────────────────────
  {
    name: 'Instagram Reel',
    category: 'video',
    description: '1080 x 1920',
    width: 1080,
    height: 1920,
    elements: [
      fullImage(1080, 1920),
      overlay(1480, 1080, 440, 0.6),
      title(56, 1530, 968, 130, 46),
      price(56, 1710, 400, 70, 56),
      brand(56, 1820, 400, 50, 24, '#d1d5db'),
    ],
  },
  {
    name: 'Instagram Story',
    category: 'social',
    description: '1080 x 1920',
    width: 1080,
    height: 1920,
    elements: [
      fullImage(1080, 1920),
      overlay(1520, 1080, 400, 0.55),
      title(56, 1560, 968, 120, 42),
      price(56, 1730, 400, 70, 52, '#34d399'),
    ],
  },
  {
    name: 'TikTok Video',
    category: 'video',
    description: '1080 x 1920',
    width: 1080,
    height: 1920,
    elements: [
      fullImage(1080, 1920),
      overlay(1460, 1080, 460, 0.6),
      title(56, 1510, 860, 130, 46),
      price(56, 1700, 400, 70, 56, '#fbbf24'),
      brand(56, 1810, 400, 50, 24, '#d1d5db'),
    ],
  },
  {
    name: 'YouTube Thumbnail',
    category: 'video',
    description: '1280 x 720',
    width: 1280,
    height: 720,
    elements: [
      fullImage(1280, 720),
      overlay(480, 1280, 240, 0.7),
      title(48, 510, 1184, 80, 40),
      price(48, 620, 350, 56, 44, '#fbbf24'),
    ],
  },
];

export const TEMPLATE_CATEGORIES: { key: CanvasTemplate['category']; label: string }[] = [
  { key: 'shopping', label: 'Shopping' },
  { key: 'social', label: 'Social Media' },
  { key: 'video', label: 'Video / Reels' },
];
